/**
 * PersonCard — 주소를 누르면 뜨는 사람 카드 (정본 **F3-2**, 2026-09-08).
 *
 * 층은 넷이다:
 *   ① 신원   아바타 + 이름 + 주소 (+ 소속). 이름을 모르면 **「(이름없음)」**을 적는다.
 *   ② 상태   이름 밑 **배지** — 「개인 주소록」 · 「차단됨」.
 *   ③ 행동   **아이콘 셋**(lg 40 정사각) — 메일 보내기 · 담기(또는 열기) · 차단.
 *   ④ 찾기   「이 사람이 보낸 메일」 · 「내가 보낸 메일」.
 *
 * ★★★ **상태를 단추로 두지 않는다.** 아이콘만 남은 단추는 «지금 무엇을 할 수 있나»의
 *    자리다. 담긴 사람에게 「주소록에 있음」 단추를 두면 **눌러도 아무 일이 없는 단추**가
 *    생긴다 — 그 자리는 「연락처 열기」로 바뀌고, 담겼다는 사실은 배지가 말한다.
 * ★★★ **아이콘 셋을 넘기지 않는다.** 넷이 서면 그건 카드가 아니라 메뉴(F4)다.
 * ★★ iconOnly 는 `aria-label` 필수 — `title=` 로 대체하지 않는다(정본 D1 · F5).
 * ★★ 팝오버는 `createPortal(document.body)` 로 띄운다. 목록 줄은 `overflow` 상자 안이라
 *    그 안에서 열면 잘린다(정본 F3 과 같은 이유).
 *
 * 여는 쪽은 render-prop 이다 — 트리거의 «모양»은 화면마다 다르기 때문이다
 * (메일 목록은 행 버튼의 형제로 겹쳐 세우고, 상세는 주소 줄 안에 둔다).
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Mail, UserPlus, Contact as ContactIcon, Search, ArrowRight, Ban } from '../../icons';
import { useAnchoredMenu } from '../../hooks/useAnchoredMenu';
import { contactsApi, blockAddress, type BlockResult, type ContactScope } from '../../api/contacts';
import { tContacts } from '../../lib/contacts-i18n';
import './PersonCard.css';

/**
 * 카드 상자 크기 — `useAnchoredMenu` 가 **첫 좌표**를 잡을 때 쓰는 어림값이다.
 *
 * ⚠⚠ **적어 둔 높이 ≠ 렌더 높이.** 카드는 담긴 사람이면 배지가, 사내 사람이면 소속 줄이
 *    더 붙어 높이가 달라진다. 이 숫자만 믿으면 **아래가 잘려 아이콘 줄이 안 보인다**
 *    (2026-09-08 사용자 지적). 그래서 아래 `useLayoutEffect` 가 **그린 뒤 실제로 재서**
 *    창 밖으로 나간 만큼 끌어올린다.
 *
 * ⚠⚠⚠ **너무 크게 적으면 «잘림»이 아니라 «엉뚱한 자리»가 된다.** 처음엔 260 이라고 적었는데
 *    실측은 **183** 이었다(담기지 않은 사람 기준). `useAnchoredMenu` 는 이 값으로 「아래가
 *    모자라나」를 판단하므로, 부풀려 적으면 **아직 자리가 있는데도 위로 뒤집고** 그 위치도
 *    260 만큼 올려 잡는다 — 실측에서 카드가 누른 줄보다 **287px 위**에 떴다. 눌렀는데 카드가
 *    그 자리에 없으니 「안 보인다」로 읽힌다.
 * ★ 그래서 **실측 최소값**을 적고, 배지·소속·안내문으로 더 커진 만큼은 `lift` 가 잡는다.
 *   부족하게 적는 쪽이 안전하다 — 넘치면 끌어올릴 수 있지만, 일찍 뒤집힌 것은 되돌릴 수 없다.
 */
const CARD_SIZE = { width: 284, height: 184 };

/** 창 가장자리에서 이만큼은 띄운다 — `useAnchoredMenu` 의 EDGE 와 같은 뜻. */
const VIEWPORT_EDGE = 8;

export interface PersonCardProps {
  /** 이 카드가 말하는 주소. 비어 있으면 카드를 열지 않는다. */
  email: string;
  /** 메일이 알고 있는 이름 — 주소록에 담긴 이름이 있으면 **그쪽이 이긴다**. */
  name?: string | null;
  /** 「다온소프트 · 영업본부」 같은 한 줄. 없으면 그리지 않는다. */
  orgLine?: string | null;
  /** 나 자신인가 — 나에게는 담기도 차단도 뜻이 없다. */
  isMe?: boolean;
  onSendMail: (email: string) => void;
  /** 「주소록에 추가」 — 부모가 편집 팝업을 연다. */
  onAdd: (email: string, name: string | null) => void;
  /** 「연락처 열기」 — 이미 담긴 사람. */
  onOpenContact: (contactId: string) => void;
  /** 「이 사람이 보낸 메일」 · 「내가 보낸 메일」. 없으면 그 줄을 안 그린다. */
  onSearchFrom?: (email: string) => void;
  onSearchTo?: (email: string) => void;
  /** 담김·차단이 바뀌면 부모에게 알린다(목록의 배지를 다시 그리게). */
  onChanged?: () => void;
  /**
   * 트리거 — `ref` 를 달고 `toggle` 을 눌러 연다.
   *
   * ⚠ **`<button>` 이 아닐 수도 있다.** 메일 목록은 **행 전체가 이미 `<button>`** 이라
   *   그 안에 버튼을 넣을 수 없다(HTML 이 깨지고 행 클릭이 먹힌다). 그 자리는
   *   `<span role="button" tabIndex={0}>` 으로 세우고 클릭을 멈춰 세운다
   *   (`stopPropagation`). 그래서 ref 타입을 `HTMLElement` 로 넓혀 둔다.
   */
  children: (api: {
    ref: React.RefObject<HTMLElement>;
    toggle: () => void;
    open: boolean;
  }) => ReactNode;
}

interface CardState {
  contact: { id: string; scope: ContactScope; name: string | null } | null;
  blocked: boolean;
  /** 아직 물어보지 않았다 — 「담기냐 열기냐」를 성급히 그리지 않게 한다. */
  loaded: boolean;
}

const EMPTY: CardState = { contact: null, blocked: false, loaded: false };

export function PersonCard(props: PersonCardProps) {
  const { email, name, orgLine, isMe = false, onChanged } = props;
  const { anchorRef, pos, open, toggle, close } = useAnchoredMenu<HTMLElement>(CARD_SIZE, { align: 'start' });
  const [state, setState] = useState<CardState>(EMPTY);
  const [notice, setNotice] = useState<string | null>(null);
  /** 열린 뒤에도 살아 있는지 — 닫힌 카드에 setState 하지 않는다. */
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  /* 열 때마다 새로 묻는다 — 다른 화면에서 담았을 수 있다. */
  useEffect(() => {
    if (!open || email === '') return;
    let cancelled = false;
    setState(EMPTY);
    setNotice(null);
    void contactsApi.lookup([email])
      .then((res) => {
        if (cancelled || !aliveRef.current) return;
        const key = email.trim().toLowerCase();
        setState({
          contact: res.contacts[key] ?? null,
          blocked: res.blocked.includes(key),
          loaded: true,
        });
      })
      /*
       * ★ 물어보지 못해도 카드는 뜬다 — 이름과 주소는 이미 손에 있고,
       *   「메일 보내기」는 담김 여부와 상관없이 할 수 있다. 여기서 카드를 통째로
       *   닫으면 사용자는 «이름을 눌렀는데 아무 일도 안 일어난다»로 읽는다.
       */
      .catch(() => { if (!cancelled && aliveRef.current) setState({ ...EMPTY, loaded: true }); });
    return () => { cancelled = true; };
  }, [open, email]);

  const handleBlock = useCallback(async () => {
    const res: BlockResult = await blockAddress(email);
    if (!aliveRef.current) return;
    const msg: Record<BlockResult, string> = {
      BLOCKED: tContacts('contacts.block.done'),
      ALREADY: tContacts('contacts.block.already'),
      IN_ALLOW_LIST: tContacts('contacts.block.in_allow'),
      SELF: tContacts('contacts.block.self'),
      FAILED: tContacts('contacts.block.failed'),
    };
    setNotice(msg[res]);
    if (res === 'BLOCKED' || res === 'ALREADY') {
      setState((s) => ({ ...s, blocked: true }));
      onChanged?.();
    }
  }, [email, onChanged]);

  /*
   * 그린 뒤 **실제 높이로 다시 잡는다.**
   * ★ 위로 뒤집지 않고 «끌어올린다» — 뒤집으면 트리거와 카드 사이가 멀어져, 목록에서
   *   어느 줄을 눌렀는지 눈으로 잃는다. 창 안에 들어가는 만큼만 올린다.
   */
  const cardRef = useRef<HTMLDivElement>(null);
  const [lift, setLift] = useState(0);
  useLayoutEffect(() => {
    if (!pos) { setLift(0); return; }
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const over = r.bottom - (window.innerHeight - VIEWPORT_EDGE);
    // 창 위쪽으로 밀려나지 않는 선까지만 올린다.
    setLift(over > 0 ? Math.min(over, Math.max(0, pos.top - VIEWPORT_EDGE)) : 0);
  }, [pos, state.contact, state.blocked, notice]);

  const displayName = state.contact?.name?.trim() || name?.trim() || '';
  const hasName = displayName !== '';
  const initial = hasName ? displayName.slice(0, 1) : '?';

  /**
   * ★★★ **포털이어도 이벤트는 «React 트리» 위로 올라간다** (2026-09-08 사용자 지적).
   *
   * 카드는 `createPortal(document.body)` 로 그리지만, React 에서는 여전히 트리거의
   * **자손**이다. 메일 목록은 **행 전체가 `<button>`** 이라, 카드 안의 아이콘을 누르면
   * 그 클릭이 행까지 올라가 **뒤에서 메일 상세가 열렸다**. 바깥을 눌러 닫을 때도 같았다.
   *
   * ★ 그래서 카드와 가림막이 **제 자리에서 전파를 끊는다.** `click` 만으로는 모자라다 —
   *   목록은 `pointerdown`(끌어 고르기)과 `mousedown` 도 듣는다.
   * ★ DOM 으로는 이미 body 밑이라 «아래 요소가 눌리는» 문제는 아니다. 순수하게 React 의
   *   합성 이벤트 경로를 끊는 일이다.
   */
  const stopAll = {
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
    onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
  };

  const card = pos && (
    <>
      {/* 바깥을 누르면 닫힌다 — 팝오버는 포커스를 가두지 않는다(정본 F3). */}
      <div
        className="wc-pcard__scrim"
        aria-hidden
        {...stopAll}
        onClick={(e) => { e.stopPropagation(); close(); }}
      />
      <div
        ref={cardRef}
        className="wc-pcard"
        style={{ top: pos.top - lift, left: pos.left }}
        role="dialog"
        aria-label={hasName ? displayName : email}
        {...stopAll}
      >
        <div className="wc-pcard__top">
          <span className="wc-pcard__av" aria-hidden>{initial}</span>
          <span className="wc-pcard__id">
            <span className={`wc-pcard__name${hasName ? '' : ' wc-pcard__name--none'}`}>
              {hasName ? displayName : tContacts('contacts.noname')}
            </span>
            <span className="wc-pcard__mail" title={email}>{email}</span>
            {orgLine && <span className="wc-pcard__org">{orgLine}</span>}
            {(state.contact || state.blocked) && (
              <span className="wc-pcard__badges">
                {state.contact && (
                  <span className="wc-pcard__badge wc-pcard__badge--ok">
                    {tContacts(state.contact.scope === 'SHARED' ? 'contacts.card.in_shared' : 'contacts.card.in_personal')}
                  </span>
                )}
                {state.blocked && (
                  <span className="wc-pcard__badge wc-pcard__badge--dg">
                    {tContacts('contacts.card.blocked')}
                  </span>
                )}
              </span>
            )}
          </span>
        </div>

        {/* ③ 행동 — 아이콘 셋. 나 자신에게는 「메일 보내기」만 남는다. */}
        <div className="wc-pcard__acts">
          <button
            type="button"
            className="wc-pcard__act"
            aria-label={tContacts('contacts.card.send')}
            title={tContacts('contacts.card.send')}
            onClick={() => { close(); props.onSendMail(email); }}
          >
            <Mail size={18} aria-hidden />
          </button>
          {!isMe && (
            state.contact
              ? (
                <button
                  type="button"
                  className="wc-pcard__act"
                  aria-label={tContacts('contacts.card.open')}
                  title={tContacts('contacts.card.open')}
                  onClick={() => { const id = state.contact!.id; close(); props.onOpenContact(id); }}
                >
                  <ContactIcon size={18} aria-hidden />
                </button>
              )
              : (
                <button
                  type="button"
                  className="wc-pcard__act"
                  aria-label={tContacts('contacts.card.add')}
                  title={tContacts('contacts.card.add')}
                  disabled={!state.loaded}
                  onClick={() => { close(); props.onAdd(email, name ?? null); }}
                >
                  <UserPlus size={18} aria-hidden />
                </button>
              )
          )}
          {!isMe && (
            <button
              type="button"
              className="wc-pcard__act wc-pcard__act--qdanger"
              aria-label={tContacts('contacts.card.block')}
              title={tContacts('contacts.card.block')}
              onClick={() => { void handleBlock(); }}
            >
              <Ban size={18} aria-hidden />
            </button>
          )}
        </div>

        {notice && <p className="wc-pcard__notice" role="status">{notice}</p>}

        {(props.onSearchFrom || props.onSearchTo) && (
          <div className="wc-pcard__find">
            {props.onSearchFrom && (
              <button
                type="button"
                className="wc-pcard__f"
                onClick={() => { close(); props.onSearchFrom!(email); }}
              >
                <Search size={15} aria-hidden />
                {tContacts('contacts.card.from_them')}
              </button>
            )}
            {props.onSearchTo && (
              <button
                type="button"
                className="wc-pcard__f"
                onClick={() => { close(); props.onSearchTo!(email); }}
              >
                <ArrowRight size={15} aria-hidden />
                {tContacts('contacts.card.to_them')}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {props.children({ ref: anchorRef, toggle, open })}
      {card && createPortal(card, document.body)}
    </>
  );
}
